// Delivery scheduling rules + the cart's delivery date picker.
//
// Rules confirmed by the client:
//   Morning   (7am-12pm) — cutoff 12:01pm the day prior
//   Afternoon (12pm-4pm) — cutoff 3:01pm the day prior
//   Same day             — cutoff 9:01am, afternoon window only
//   Saturday             — same rules as a weekday
//   Sunday               — a single 9am-3pm window, cutoff 2:01pm the day prior
//
// On top of those rules the shop can block out individual dates (public
// holidays, shutdown periods) from the theme editor:
//   Online Store > Themes > Customize > Theme settings > Delivery.
// Those dates arrive here as window.FLINTSTONES_DELIVERY_SETTINGS, set by
// sections/main-cart.liquid.
//
// "Cutoff at 12:01pm" means the order has to be placed *before* 12:01pm, so
// every cutoff comparison below is a strict less-than.
window.FlintstonesDelivery = (function () {
  var MORNING = "7am-12pm";
  var AFTERNOON = "12pm-4pm";
  var SUNDAY = "9am-3pm";

  // Cutoffs expressed as minutes past midnight.
  var CUTOFF_SAME_DAY = 9 * 60 + 1; // 9:01am
  var CUTOFF_PRIOR_MORNING = 12 * 60 + 1; // 12:01pm
  var CUTOFF_PRIOR_SUNDAY = 14 * 60 + 1; // 2:01pm
  var CUTOFF_PRIOR_AFTERNOON = 15 * 60 + 1; // 3:01pm

  var LABELS = {};
  LABELS[MORNING] = "Morning (7am - 12pm)";
  LABELS[AFTERNOON] = "Afternoon (12pm - 4pm)";
  LABELS[SUNDAY] = "Sunday (9am - 3pm)";

  function toDateStr(date) {
    var y = date.getFullYear();
    var m = String(date.getMonth() + 1).padStart(2, "0");
    var d = String(date.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + d;
  }

  function parseDateStr(str) {
    var parts = String(str).split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  // Whole calendar days from `from` to `to`. Rounded so a daylight-saving
  // change can't push the result to 0.96 or 1.04 of a day.
  function daysBetween(from, to) {
    return Math.round((startOfDay(to) - startOfDay(from)) / 86400000);
  }

  function minutesNow(now) {
    return now.getHours() * 60 + now.getMinutes();
  }


  // ---------------------------------------------------------------------------
  // Blocked dates, entered by the shop in the theme editor.
  //
  // The textarea takes one entry per line, each either a single date or a
  // range, in DD/MM/YYYY. Anything after a "#" is the shop's own note:
  //
  //   25/12/2026            # Christmas Day
  //   24/12/2026 to 26/12/2026
  //
  // Anything unparseable is skipped rather than blocking the wrong day: a typo
  // in the list should lose that one holiday, not the whole calendar.
  // ---------------------------------------------------------------------------

  var DMY = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/;
  var YMD = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

  function parseEntryDate(str) {
    var text = String(str).trim();
    var m = text.match(DMY);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    m = text.match(YMD);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return null;
  }

  function isRealDate(date, str) {
    // new Date(2026, 1, 30) silently rolls over to 2 March. Round-tripping the
    // day catches that, so "30/02/2026" is dropped instead of blocking March.
    var m = String(str).trim().match(DMY) || String(str).trim().match(YMD);
    if (!date || isNaN(date.getTime()) || !m) return false;
    var day = m[0].indexOf("-") === 4 ? Number(m[3]) : Number(m[1]);
    return date.getDate() === day;
  }

  function parseBlockedDates(raw) {
    if (!raw) return [];

    return String(raw)
      .split(/[\r\n]+/)
      .map(function (line) {
        return line.split("#")[0].trim(); // strip the shop's own notes
      })
      .filter(Boolean)
      .map(function (line) {
        var parts = line.split(/\s+to\s+|\s+-\s+|\s*\u2013\s*/i);
        var from = parseEntryDate(parts[0]);
        if (!isRealDate(from, parts[0])) return null;

        if (parts.length < 2) return { from: from, to: from };

        var to = parseEntryDate(parts[1]);
        if (!isRealDate(to, parts[1]) || to < from) return null;
        return { from: from, to: to };
      })
      .filter(Boolean);
  }

  // Parsed once the settings are on the page, then cached against the raw text
  // so an edit in the theme editor's live preview is picked up.
  var blockedCache = { raw: null, ranges: [] };

  function blockedRanges() {
    var settings = window.FLINTSTONES_DELIVERY_SETTINGS || {};
    var raw = settings.blockedDates || "";
    if (raw !== blockedCache.raw) {
      blockedCache = { raw: raw, ranges: parseBlockedDates(raw) };
    }
    return blockedCache.ranges;
  }

  function isBlocked(date) {
    var day = startOfDay(date).getTime();
    return blockedRanges().some(function (range) {
      return day >= range.from.getTime() && day <= range.to.getTime();
    });
  }

  // The delivery windows still bookable for `dateStr`, given the current time.
  // An empty array means that date can no longer be delivered on.
  function availableWindows(dateStr, now) {
    now = now || new Date();
    if (!dateStr) return [];

    var date = parseDateStr(dateStr);
    if (!date) return [];

    var daysAhead = daysBetween(now, date);
    if (daysAhead < 0) return [];
    if (isBlocked(date)) return [];

    var mins = minutesNow(now);

    // Sunday runs a single window and takes its orders by 2:01pm on Saturday.
    // There is no same-day Sunday delivery — the window opens at 9am, which is
    // before the 9:01am same-day cutoff would even apply.
    if (date.getDay() === 0) {
      if (daysAhead === 0) return [];
      if (daysAhead === 1 && mins >= CUTOFF_PRIOR_SUNDAY) return [];
      return [SUNDAY];
    }

    // Same day: afternoon only, ordered before 9:01am.
    if (daysAhead === 0) {
      return mins < CUTOFF_SAME_DAY ? [AFTERNOON] : [];
    }

    // Next day: each window has its own cutoff on the day prior, i.e. today.
    if (daysAhead === 1) {
      var windows = [];
      if (mins < CUTOFF_PRIOR_MORNING) windows.push(MORNING);
      if (mins < CUTOFF_PRIOR_AFTERNOON) windows.push(AFTERNOON);
      return windows;
    }

    // Two or more days out, every cutoff is comfortably met.
    return [MORNING, AFTERNOON];
  }

  function isDateAvailable(date, now) {
    return availableWindows(toDateStr(date), now).length > 0;
  }

  function isWindowAvailable(dateStr, window, now) {
    return availableWindows(dateStr, now).indexOf(window) !== -1;
  }

  // Why `dateStr` can't take `window`, phrased for the customer.
  function unavailableReason(dateStr, window, now) {
    now = now || new Date();
    var date = parseDateStr(dateStr);
    if (!date) return "Please select a delivery date.";

    var daysAhead = daysBetween(now, date);
    if (daysAhead < 0) return "Please select a delivery date in the future.";
    if (isBlocked(date))
      return "We're not delivering on that date. Please choose another day.";

    if (date.getDay() === 0) {
      if (daysAhead === 0) {
        return "Sunday deliveries need to be booked by 2:01pm the day before. Please choose another date.";
      }
      if (window !== SUNDAY) {
        return "Sunday has one delivery window only: 9am - 3pm.";
      }
      return "Orders for Sunday delivery need to be placed before 2:01pm on Saturday. Please choose another date.";
    }

    if (daysAhead === 0) {
      if (window === MORNING) {
        return "Morning delivery isn't available same day. Same-day orders are afternoon only, placed before 9:01am.";
      }
      return "Same-day delivery needs to be ordered before 9:01am. Please choose another date.";
    }

    if (window === MORNING) {
      return "Morning delivery (7am - 12pm) needs to be ordered before 12:01pm the day before.";
    }
    if (window === AFTERNOON) {
      return "Afternoon delivery (12pm - 4pm) needs to be ordered before 3:01pm the day before.";
    }
    return "That delivery window isn't available for the date you've chosen.";
  }

  return {
    MORNING: MORNING,
    AFTERNOON: AFTERNOON,
    SUNDAY: SUNDAY,
    LABELS: LABELS,
    toDateStr: toDateStr,
    parseDateStr: parseDateStr,
    availableWindows: availableWindows,
    isDateAvailable: isDateAvailable,
    isWindowAvailable: isWindowAvailable,
    unavailableReason: unavailableReason,
    isBlocked: isBlocked,
    parseBlockedDates: parseBlockedDates,
  };
})();

// Initialize Flatpickr calendar for delivery date selection
document.addEventListener("DOMContentLoaded", function () {
  const deliveryDateInput = document.getElementById("delivery-date");

  if (deliveryDateInput && typeof flatpickr !== "undefined") {
    flatpickr(deliveryDateInput, {
      minDate: "today",
      dateFormat: "Y-m-d",
      disableMobile: true, // Force desktop calendar even on mobile
      onChange: function (selectedDates, dateStr, instance) {
        // Update the cart attribute when date changes
        if (dateStr) {
          updateCartAttribute("Preferred Delivery Date", dateStr);
        }
      },
      disable: [
        function (date) {
          // Grey out any date with no bookable window left — today after the
          // 9:01am cutoff, tomorrow after 3:01pm, Sunday after Saturday 2:01pm.
          return !window.FlintstonesDelivery.isDateAvailable(date);
        },
      ],
    });
  }
});

// Function to update cart attributes via AJAX
function updateCartAttribute(key, value) {
  const formData = {
    attributes: {
      [key]: value,
    },
  };

  fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating cart attribute:", error));
}
